// FEED-mode grid sizing: lay N participant feeds into an area so that tiles
// are as large as possible, share one aspect ratio within [4:3, 16:9], stay
// at least `minWidth` wide, and are distributed as evenly as possible per row.
//
// Split into plan (choose grid + tile size) and place (position tiles) so the
// split layout can read a grid's content size before committing to positions.

import {
    DEFAULT_GAP,
    FeedGridResult,
    MAX_ASPECT,
    MIN_ASPECT,
    MIN_FEED_WIDTH,
    Rect,
    Size,
    TilePlacement,
    clamp,
    fitAspectInBox,
} from './types';

export interface FeedGridPlan {
    cols: number; // effective max columns (widest row)
    rows: number;
    rowCounts: number[]; // tiles per row (even; sums to `cells`)
    tileWidth: number;
    tileHeight: number;
    tileAspect: number;
    cells: number; // placed cells (incl. a "+N" cell when overflowing)
    visibleCount: number; // real feed tiles
    overflowCount: number; // feeds not shown
}

export interface FeedGridOptions {
    minWidth?: number;
    gap?: number;
    /**
     * Cap on how many real face tiles to place, independent of how many would
     * geometrically fit. `count` still drives the overflow tally, so the "+N"
     * indicator reflects every feed not shown — those hidden by the container
     * (min width) AND those hidden by this render cap. Used to bound the number
     * of live video tiles (and decodes) while keeping the count honest.
     */
    maxVisible?: number;
}

const EMPTY_PLAN: FeedGridPlan = {
    cols: 0,
    rows: 0,
    rowCounts: [],
    tileWidth: 0,
    tileHeight: 0,
    tileAspect: MAX_ASPECT,
    cells: 0,
    visibleCount: 0,
    overflowCount: 0,
};

/** Spread `count` across `rows` rows so row sizes differ by at most one. */
const distributeRows = (count: number, rows: number): number[] => {
    const base = Math.floor(count / rows);
    const extra = count % rows;
    return Array.from({ length: rows }, (_, i) => base + (i < extra ? 1 : 0));
};

interface GridCandidate {
    cols: number;
    rows: number;
    tileWidth: number;
    tileHeight: number;
    tileAspect: number;
    area: number;
}

/** Best uniform-tile grid for exactly `cells` cells, or null if none fits the min width. */
const bestGridForCells = (
    area: Size,
    cells: number,
    gap: number,
    minWidth: number,
): GridCandidate | null => {
    let best: GridCandidate | null = null;
    const seen = new Set<string>();

    for (let cols = 1; cols <= cells; cols++) {
        const rows = Math.ceil(cells / cols);
        // Canonical minimal columns for this row count (dedupes wasteful cols,
        // e.g. 4 cells never need 3 columns — they balance to 2×2).
        const effCols = Math.ceil(cells / rows);
        const key = `${effCols}x${rows}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const cellWidth = (area.width - (effCols - 1) * gap) / effCols;
        const cellHeight = (area.height - (rows - 1) * gap) / rows;
        if (cellWidth <= 0 || cellHeight <= 0) continue;

        const tileAspect = clamp(
            cellWidth / cellHeight,
            MIN_ASPECT,
            MAX_ASPECT,
        );
        const tile = fitAspectInBox(cellWidth, cellHeight, tileAspect);
        if (tile.width < minWidth) continue;

        const candidate: GridCandidate = {
            cols: effCols,
            rows,
            tileWidth: tile.width,
            tileHeight: tile.height,
            tileAspect,
            area: tile.width * tile.height,
        };
        const emptyCells = effCols * rows - cells;
        if (
            !best ||
            candidate.area > best.area + 0.5 ||
            // tie on size -> prefer the more even layout (fewest empty cells)
            (Math.abs(candidate.area - best.area) <= 0.5 &&
                emptyCells < best.cols * best.rows - cells)
        ) {
            best = candidate;
        }
    }
    return best;
};

/**
 * Choose a grid for `count` feeds in `area`. Honors `minWidth`; if not all
 * feeds fit at that width, shows as many as fit and reserves the last cell as
 * a "+N" overflow indicator.
 */
export const planFeedGrid = (
    area: Size,
    count: number,
    options: FeedGridOptions = {},
): FeedGridPlan => {
    const gap = options.gap ?? DEFAULT_GAP;
    const minWidth = options.minWidth ?? MIN_FEED_WIDTH;
    if (count <= 0 || area.width <= 0 || area.height <= 0) return EMPTY_PLAN;

    // When more feeds exist than the render cap allows, only attempt to place
    // `maxVisible` faces + one "+N" cell; the true `count` still drives the
    // overflow tally below so "+N" stays honest.
    const { maxVisible } = options;
    let cellCap = count;
    let renderCapped = false;
    if (maxVisible !== undefined && count > maxVisible) {
        cellCap = maxVisible + 1;
        renderCapped = true;
    }

    let cells = cellCap;
    let grid = bestGridForCells(area, cells, gap, minWidth);
    while (!grid && cells > 1) {
        cells -= 1;
        grid = bestGridForCells(area, cells, gap, minWidth);
    }
    // Degenerate: container too small for even one min-width tile -> place a
    // single tile that simply fits the container.
    if (!grid) {
        grid = bestGridForCells(area, 1, gap, 0);
        cells = 1;
    }
    if (!grid) return EMPTY_PLAN;

    // Overflowing if the render cap hid some, or the container couldn't fit all.
    const overflowing = renderCapped || cells < count;
    // Reserve the last cell as the "+N" indicator only when at least one other
    // cell remains to show a real face. With a single cell (a container too
    // small to fit even one min-width tile), show that face rather than a lone
    // "+N" badge with no video behind it.
    const reserveOverflowCell = overflowing && cells > 1;
    return {
        cols: grid.cols,
        rows: grid.rows,
        rowCounts: distributeRows(cells, grid.rows),
        tileWidth: grid.tileWidth,
        tileHeight: grid.tileHeight,
        tileAspect: grid.tileAspect,
        cells,
        visibleCount: reserveOverflowCell ? cells - 1 : cells,
        overflowCount: reserveOverflowCell ? count - (cells - 1) : 0,
    };
};

/** The grid's tight content box (no surrounding slack). */
export const planContentSize = (
    plan: FeedGridPlan,
    gap = DEFAULT_GAP,
): Size => ({
    width:
        plan.cols > 0 ? plan.cols * plan.tileWidth + (plan.cols - 1) * gap : 0,
    height:
        plan.rows > 0 ? plan.rows * plan.tileHeight + (plan.rows - 1) * gap : 0,
});

/** Position a plan's cells inside `area`: rows centered, block centered. */
export const placeFeedGrid = (
    area: Rect,
    plan: FeedGridPlan,
    gap = DEFAULT_GAP,
): TilePlacement[] => {
    if (plan.cells <= 0) return [];
    const { rows, rowCounts, tileWidth, tileHeight } = plan;
    const gridHeight = rows * tileHeight + (rows - 1) * gap;
    const blockY = area.y + Math.max(0, (area.height - gridHeight) / 2);

    const tiles: TilePlacement[] = [];
    let placed = 0;
    for (let r = 0; r < rows; r += 1) {
        const inRow = rowCounts[r];
        const rowWidth = inRow * tileWidth + (inRow - 1) * gap;
        const rowX = area.x + Math.max(0, (area.width - rowWidth) / 2);
        const y = blockY + r * (tileHeight + gap);
        for (let c = 0; c < inRow && placed < plan.cells; c += 1) {
            tiles.push({
                index: placed,
                rect: {
                    x: rowX + c * (tileWidth + gap),
                    y,
                    width: tileWidth,
                    height: tileHeight,
                },
            });
            placed += 1;
        }
    }
    return tiles;
};

/** Plan + place in one call (FEED mode). */
export const computeFeedGrid = (
    area: Rect,
    count: number,
    options: FeedGridOptions = {},
): FeedGridResult => {
    const gap = options.gap ?? DEFAULT_GAP;
    const plan = planFeedGrid(
        { width: area.width, height: area.height },
        count,
        options,
    );
    return {
        cols: plan.cols,
        rows: plan.rows,
        tileWidth: plan.tileWidth,
        tileHeight: plan.tileHeight,
        tileAspect: plan.tileAspect,
        visibleCount: plan.visibleCount,
        overflowCount: plan.overflowCount,
        tiles: placeFeedGrid(area, plan, gap),
    };
};
